<div class="card-content" [ngClass]="{'selected-card': isSelected()}" [style.background-color]="isLoading() ? '#E7E5E4' : color()">
  <!-- Loading Shimmer -->
  @if (isLoading()) {
    <div class="card-shimmer-container">
      <div class="shimmer-animation"></div>
    </div>
  }

  <!-- Error State -->
  @if (hasError() && !isLoading()) {
    <div class="flex flex-col h-full">
      <div class="flex justify-between items-center w-full mb-4 card-error-header">
        <div class="flex w-3/4 items-center">
          <ui-icon [name]="cardType() === 'REPORT' ? 'reports' : (cardType() === 'ANSWER' ? 'data-copilot' : 'notebook-pen')" class="mr-3 stroke-red-500 stroke-2 transition-colors duration-200" />

          <h3 class="text-lg font-medium text-red-600 text-ellipsis overflow-hidden whitespace-nowrap" title="{{cardTitle()}} (Error)">
            {{cardTitle()}}
          </h3>

          <div class="ml-2 flex items-center">
            <ui-icon name="alert-triangle" size="size-4" class="stroke-red-500" title="Card has errors"></ui-icon>
          </div>
        </div>

        <div class="flex gap-2 justify-center items-center">
          <button (click)="onDelete()" class="flex p-1 rounded error-delete-button" title="Delete card">
            <ui-icon name="trash" size="size-4" class="stroke-red-500 hover:stroke-red-600"></ui-icon>
          </button>

          <div class="flex items-center">
            <input type="checkbox"
                   [ngModel]="isSelected()"
                   (ngModelChange)="onSelectionChange()"
                   class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer">
          </div>
        </div>
      </div>
      <div class="flex-1 flex flex-col justify-center items-center text-center px-4">
        <ui-icon name="alert-circle" size="size-12" class="stroke-red-500 mb-3 opacity-60"></ui-icon>
        <p class="text-red-600 font-medium mb-2">Something went wrong</p>
        <p class="text-red-500 text-sm opacity-80">{{ errorMessage() }}</p>
      </div>
    </div>
  }

  <!-- Success State Header and Content -->
  <div [class.hidden]="isLoading() || hasError()" class="h-full">
    <div class="flex justify-between items-center w-full mb-2" [ngClass]="{'mb-4': cardType() === 'ANSWER'}">
      <div class="flex items-center min-w-0 flex-1 mr-2">
        <ui-icon [name]="cardType() === 'REPORT' ? 'reports' : (cardType() === 'ANSWER' ? 'data-copilot' : 'notebook-pen')" class="mr-2 stroke-current stroke-2 transition-colors duration-200" />

        <!-- Free Form Card Title (with editing functionality) -->
        @if (cardType() === 'FREEFORM_CARD') {
          <div class="flex items-center min-w-0 flex-1">
            <!-- Display Mode -->
            @if (!isEditingTitle()) {
              <div class="flex items-center min-w-0 flex-1">
                <h3 class="text-lg font-medium text-ellipsis overflow-hidden whitespace-nowrap min-w-0 flex-1 freeform-card-title"
                    [class.clickable]="isSaved"
                    [class.non-clickable]="!isSaved"
                    [class.cursor-pointer]="isSaved"
                    [class.cursor-default]="!isSaved"
                    [title]="isSaved ? 'Double-click to edit title' : cardTitle()"
                    (dblclick)="isSaved ? onEditTitleClick() : null">
                  {{cardTitle()}}
                </h3>
              </div>
            }

            <!-- Edit Mode -->
            @if (isEditingTitle()) {
              <div class="flex items-center min-w-0 flex-1 gap-2">
                <input
                  type="text"
                  [ngModel]="editingTitle()"
                  (ngModelChange)="editingTitle.set($event)"
                  class="title-input no-drag min-w-0 flex-1 px-2 py-1 text-lg font-medium border rounded focus:outline-none focus:ring-1"
                  [class.border-gray-300]="editingTitle().trim().length > 0"
                  [class.focus:border-blue-500]="editingTitle().trim().length > 0"
                  [class.focus:ring-blue-500]="editingTitle().trim().length > 0"
                  [class.border-red-300]="editingTitle().trim().length === 0"
                  [class.focus:border-red-500]="editingTitle().trim().length === 0"
                  [class.focus:ring-red-500]="editingTitle().trim().length === 0"
                  placeholder="Enter card title"
                  (mousedown)="$event.stopPropagation();"
                  (keydown)="onTitleKeydown($event)"
                  required>
                <button
                  class="p-1 rounded title-edit-button"
                  [class.hover:bg-green-100]="editingTitle().trim().length > 0"
                  [class.cursor-pointer]="editingTitle().trim().length > 0"
                  [class.opacity-50]="editingTitle().trim().length === 0"
                  [class.cursor-not-allowed]="editingTitle().trim().length === 0"
                  [disabled]="editingTitle().trim().length === 0"
                  (click)="onTitleSave(); $event.stopPropagation()"
                  title="Save title">
                  <ui-icon name="check" class="stroke-green-600" size="size-5"></ui-icon>
                </button>
                <button
                  class="p-1 rounded hover:bg-red-100 title-edit-button"
                  (click)="onTitleCancel(); $event.stopPropagation()"
                  title="Cancel">
                  <ui-icon name="close" class="stroke-red-600" size="size-5"></ui-icon>
                </button>
              </div>
            }
          </div>
        }

        <!-- Other Card Types Title -->
        @if (cardType() !== 'FREEFORM_CARD') {
          <h3 class="text-lg font-medium text-ellipsis overflow-hidden whitespace-nowrap min-w-0 flex-1" title="{{cardTitle()}}">
            {{cardTitle()}}
          </h3>
        }

        @if (cardType() === 'REPORT' && reportDescription()) {
          <div class="ml-2 relative group flex items-center">
            <ui-icon name="info" class="stroke-gray-500 hover:stroke-blue-500 cursor-pointer" size="w-4 h-4"></ui-icon>
            <div class="absolute z-10 hidden group-hover:block bg-gray-800 text-white text-sm rounded p-2 min-w-[200px] max-w-[300px] shadow-lg -left-2 top-6">
              {{reportDescription()}}
            </div>
          </div>
        }
      </div>

      <div class="flex gap-2 justify-center items-center flex-shrink-0">
        @if (cardType() === 'REPORT') {
          <div class="flex items-center mr-3">
            <div class="flex border border-[#EAECF0] rounded-full">
              <div class="hover:bg-[#77ABF9] rounded-[300px] py-3 px-4 group cursor-pointer"
                [ngClass]="{'bg-[#77ABF9]': visualType() == 'TABLE'}"
                (click)="onVisualTypeToggle('TABLE')">
                <ui-icon name="report-table" class="stroke-[#344054] group-hover:stroke-white stroke-[1.5px]"
                  size="size-3" [ngClass]="{'stroke-white': visualType() == 'TABLE'}" />
              </div>
              <div class="hover:bg-[#77ABF9] rounded-[300px] py-3 px-4 group cursor-pointer"
                [ngClass]="{'bg-[#77ABF9]': visualType() == 'CHART'}"
                (click)="onVisualTypeToggle('CHART')">
                <ui-icon name="template-bar-chart"
                  class="stroke-[#344054] hover:stroke-[#0F49A0] group-hover:stroke-white stroke-[1.5px]"
                  size="size-3" [ngClass]="{'stroke-white': visualType() == 'CHART'}" />
              </div>
            </div>
          </div>
        }

        @if (cardType() === 'ANSWER') {
          <div class="flex items-center mr-3">
            <div class="flex border border-[#EAECF0] rounded-full">
              <div class="hover:bg-[#77ABF9] rounded-[300px] py-3 px-4 group cursor-pointer"
                [ngClass]="{'bg-[#77ABF9]': viewMode() == 'grid'}"
                (click)="onDataVisualizationToggle('grid')">
                <ui-icon name="report-table" class="stroke-[#344054] group-hover:stroke-white stroke-[1.5px]"
                  size="size-3" [ngClass]="{'stroke-white': viewMode() == 'grid'}" />
              </div>
              <div class="hover:bg-[#77ABF9] rounded-[300px] py-3 px-4 group cursor-pointer"
                [ngClass]="{'bg-[#77ABF9]': viewMode() == 'chart'}"
                (click)="onDataVisualizationToggle('chart')">
                <ui-icon name="template-bar-chart"
                  class="stroke-[#344054] hover:stroke-[#0F49A0] group-hover:stroke-white stroke-[1.5px]"
                  size="size-3" [ngClass]="{'stroke-white': viewMode() == 'chart'}" />
              </div>
            </div>
          </div>
        }

        @if (cardType() === 'REPORT' && reportId()) {
          <button (click)="navigateToReport()" class="flex">
            <ui-icon name="external-link" size="size-4" class="stroke-gray-700 hover:stroke-blue-700 mr-2"></ui-icon>
          </button>
        }
        @if (cardType() === 'FREEFORM_CARD') {
          <button (click)="onEdit()" class="flex">
            <ui-icon name="pencil" size="size-4" class="stroke-gray-700 hover:stroke-blue-700 mr-2"></ui-icon>
          </button>
        }
        <button (click)="onDelete()" class="flex">
          <ui-icon name="trash" size="size-4" class="stroke-gray-700 hover:stroke-blue-700 mr-2"></ui-icon>
        </button>
        <div class="flex items-center">
          <input type="checkbox"
                 [ngModel]="isSelected()"
                 (ngModelChange)="onSelectionChange()"
                 class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer">
        </div>
      </div>
    </div>

    <!-- Report Content -->
    @if (cardType() === 'REPORT') {
      <div class="card-data-container h-[90%]">
        <app-report-framework
          [reportId]="reportId()"
          [isCanvas]="isCanvas()"
          [reportVisualType]="visualType()"
          [canvasVisualType]="selectedVisualType()"
          [cardId]="id()"
          [canvasChartConfigurations]="chartConfigurations()"
          [canvasGridConfigurations]="gridConfigurations()"
          (reportNameEmitter)="updateCardTitle($event)"
          (templateTypesLoadingChange)="isLoading.set($event)"
          (reportDataLoaded)="onDataLoaded()"
          (reportLoadError)="onLoadError($event)"
          (visualTypeLoaded)="onVisualTypeLoaded($event)"
          (insightsGenerated)="onInsightsGenerated($event)"
          (insightsError)="onInsightsError($event)"
          (chartConfigurationsChanged)="onReportChartConfigurationsChanged($event)"
          (gridConfigurationsChanged)="onReportGridConfigurationsChanged($event)">
        </app-report-framework>
      </div>
    }

    <!-- Copilot Answer Content -->
    @if (cardType() === 'ANSWER') {
      <div class="card-data-container h-[93%] border-t border-[#EAECF0] pt-[0.7rem]">
        @if (tableData() && !hasError() && !isLoading()) {
          <app-data-visualization
            [tableData]="tableData()"
            [defaultColDef]="{ flex: 1, width: 100, resizable: true, filter: true, sortable: true }"
            [viewMode]="viewMode()"
            [showToggle]="false"
            [canvasId]="'canvas-chart-' + id()"
            [agGridChartType]="chartType()"
            [chartModel]="localChartModel()"
            [filterModel]="localFilterModel()"
            [sortModel]="localSortModel()"
            [isCanvas]="true"
            (viewModeChange)="onViewModeChange($event)"
            (chartModelChange)="onChartModelChange($event)"
            (filterChange)="onFilterChange($event)"
            (sortChange)="onSortChange($event)"
            class="w-full h-full canvas-data-visualization">
          </app-data-visualization>
        }

        @if (isLoading()) {
          <div class="loading-container h-full flex flex-col justify-center items-center">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
            <p class="text-sm text-gray-500">Loading answer data...</p>
          </div>
        }

        @if (hasError() && !isLoading()) {
          <div class="error-container h-full flex flex-col justify-center items-center text-center px-4">
            <ui-icon name="alert-circle" size="size-8" class="stroke-red-500 mb-3 opacity-60"></ui-icon>
            <p class="text-red-500 text-sm font-medium mb-1">Failed to load data</p>
            <p class="text-red-400 text-xs opacity-80">{{ errorMessage() }}</p>
          </div>
        }
      </div>
    }

    @if (cardType() === 'FREEFORM_CARD') {
      <div class="card-data-container h-[93%] border-t border-[#EAECF0] pt-[0.7rem]">
        <app-free-form-text-editor
          [cardId]="id()"
          [initialContent]="content()"
          [initialAssetId]="assetId()"
          [isLoadingContent]="isLoadingFreeForm()"
          [canvasId]="canvasId()"
          [cardTitle]="cardTitle()"
          (assetCreated)="onFreeFormAssetCreated($event)"
          (saveStateChange)="onFreeFormSaveStateChange($event)"
          (openInsightsModal)="onOpenInsightsModal()"
          (triggerCanvasSave)="onTriggerCanvasSave()">
        </app-free-form-text-editor>
      </div>
    }
  </div>
</div>
